// src/components/familytree/TreeNode.js
// Updated: 10/18/2025 02:28 PM

import React, { useMemo } from 'react';
import { FaUserCircle } from 'react-icons/fa';

// Normalize Firestore id or DocumentReference -> string id
const getId = (val) => {
  if (!val) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val.id) return val.id; // DocumentReference
  return null;
};

const PersonCard = ({ person, isHost, onNodeClick, isSelected }) => {
  const baseClasses = "flex items-center p-2 rounded-md transition-all duration-200 border bg-white";
  const hostClasses = isHost ? 'cursor-pointer hover:shadow-lg hover:border-blue-400' : '';
  const selectedClasses = isSelected ? 'border-green-500 ring-2 ring-green-500' : '';

  const handleClick = (e) => {
    if (isHost && onNodeClick) onNodeClick(person, e);
  };

  return (
    <div className={`${baseClasses} ${hostClasses} ${selectedClasses}`} onClick={handleClick}>
      {person.profilePictureUrl ? (
        <img
          src={person.profilePictureUrl}
          alt={`${person.firstName || ''} ${person.lastName || ''}`}
          className="h-10 w-10 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <FaUserCircle className="h-10 w-10 text-gray-300 flex-shrink-0" />
      )}
      <div className="ml-3 text-left">
        <p className="font-bold text-sm truncate">
          {person.firstName} {person.lastName}
        </p>
        <p className="text-xs text-gray-500">
          {person.dob}
          {person.isDeceased ? ` - ${person.dod || ''}` : ''}
        </p>
      </div>
    </div>
  );
};

const TreeNode = ({
  node,
  allNodes, // ENRICHED nodes (with children arrays)
  isHost,
  onNodeClick,
  sourcePersonId,
  visited = new Set(), // Prevent cycles
}) => {
  // Fast lookup map from enriched nodes
  const byId = useMemo(() => {
    const map = new Map();
    (allNodes || []).forEach((p) => map.set(p.id, p));
    return map;
  }, [allNodes]);

  // Bail if invalid or already visited in this path
  if (!node || visited.has(node.id)) return null;

  // Mark this node as visited for recursive calls
  const nextVisited = new Set(visited);
  nextVisited.add(node.id);

  const spouseId = getId(node.spouseId);
  const spouseNode = spouseId ? byId.get(spouseId) || null : null;

  // If spouse exists, mark as visited too to avoid couple loops
  if (spouseNode) {
    nextVisited.add(spouseNode.id);
  }

  // === MERGED CHILDREN FOR COUPLES ===
  // Union of this node's children and spouse's children (unique by id),
  // so a child shows if linked to either parent.
  const rawChildren = [
    ...(Array.isArray(node.children) ? node.children : []),
    ...(spouseNode && Array.isArray(spouseNode.children) ? spouseNode.children : []),
  ];

  const seen = new Set();
  const mergedChildren = rawChildren.filter((c) => {
    if (!c || seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });

  const childrenToRender = mergedChildren
    .filter((child) => child && !visited.has(child.id))
    .sort((a, b) => {
      const aDob = a.dob || '';
      const bDob = b.dob || '';
      if (aDob && bDob && aDob !== bDob) return aDob.localeCompare(bDob);
      const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim();
      const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim();
      return aName.localeCompare(bName);
    });

  return (
    <li>
      <div>
        {/* Node container - holds node + spouse (if any) */}
        <div className="flex flex-col gap-2">
          <PersonCard
            person={node}
            isHost={isHost}
            onNodeClick={onNodeClick}
            isSelected={node.id === sourcePersonId}
          />
          {spouseNode && !visited.has(spouseNode.id) && (
            <PersonCard
              person={spouseNode}
              isHost={isHost}
              onNodeClick={onNodeClick}
              isSelected={spouseNode.id === sourcePersonId}
            />
          )}
        </div>
      </div>

      {childrenToRender.length > 0 && (
        <ul>
          {childrenToRender.map((childNode) => (
            <TreeNode
              key={childNode.id}
              node={childNode}
              allNodes={allNodes} // keep passing enriched set
              isHost={isHost}
              onNodeClick={onNodeClick}
              sourcePersonId={sourcePersonId}
              visited={nextVisited}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default TreeNode;
